"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { HmcIcon, HmcButton } from "@adi-ctx/harmonic-core-components-react";
import { RiGithubLine, RiPagesLine } from "@remixicon/react";
import {
	DriverStatus,
	type TDropdownMenuItem,
	withDropdownMenu,
	Link,
} from "@/components";
import { t } from "@/utils";
import type { TDriver, TFilters } from "@/types";
import "./DriversList.scss";
import { pathNames } from "@/lib/constants/pathNames.constants";
import PaginationControls from "../PaginationControls/PaginationControls";

type DriverListProps = {
	readonly drivers: TDriver[];
	readonly filters: TFilters;
};

type TSortBy = "category" | "subsystem" | "partNumber";
type TSortDir = "asc" | "desc";

// The method is used for sorting the drivers (natural sort)
const naturalOrderCollator = new Intl.Collator("en", {
	numeric: true,
	sensitivity: "base",
});

// List of DropdownMenu items for sorting the drivers
const sortMenuItems: TDropdownMenuItem[] = [
	{
		id: "desc" as TSortDir,
		label: t("drivers-list.sort-desc"),
		icon: <HmcIcon name="arrow-down-m" />,
	},
	{
		id: "asc" as TSortDir,
		label: t("drivers-list.sort-asc"),
		icon: <HmcIcon name="arrow-up-m" />,
	},
];

export default function DriversList({
	drivers: defaultDrivers,
	filters,
}: DriverListProps) {
	const SortButton = withDropdownMenu(HmcButton);
	const [drivers, setDrivers] = useState<TDriver[]>(defaultDrivers);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(50);
	const tableRef = useRef<HTMLTableElement>(null);

	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentItems = drivers.slice(indexOfFirstItem, indexOfLastItem);

	const nextPage = () => {
		setCurrentPage((prev) =>
			Math.min(prev + 1, Math.ceil(drivers.length / itemsPerPage))
		);
		tableRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const firstPage = () => {
		setCurrentPage(1);
		tableRef.current?.scrollIntoView({ behavior: "smooth" });
	};
	const prevPage = () => {
		setCurrentPage((prev) => Math.max(prev - 1, 1));
		tableRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const lastPage = () => {
		setCurrentPage(Math.ceil(drivers.length / itemsPerPage));
		tableRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const [sort, setSort] = useState<
		Record<"sortBy" | "sortDir", TSortBy | TSortDir> | Record<any, any>
	>({});

	// Compose the URL for the drivers details page
	const getDriversDetailsUrl = useCallback((driver: TDriver) => {
		if (!driver.hasDetails) {
			return;
		}

		return pathNames.driversDetails
			.replace("{category}", driver.category.toLowerCase())
			.replace("{partNumber}", driver.partNumber);
	}, []);

	const onSort = useCallback(
		(sortBy: TSortBy, sortDir: TSortDir) => {
			setSort(() => {
				if (sort.sortBy === sortBy && sort.sortDir === sortDir) return {};
				return { sortBy, sortDir };
			});
		},
		[setSort, sort]
	);

	const getSortedDrivers = useCallback(
		(drivers: TDriver[]) => {
			if (!sort.sortBy) return drivers;

			drivers.sort((a, b) => {
				// asc
				if (sort.sortDir === "asc") {
					return naturalOrderCollator.compare(
						a[sort.sortBy as TSortBy]!,
						b[sort.sortBy as TSortBy]!
					);
				}

				// desc
				return naturalOrderCollator.compare(
					b[sort.sortBy as TSortBy]!,
					a[sort.sortBy as TSortBy]!
				);
			});

			return [...drivers];
		},
		[sort]
	);

	// Return the item from "sortMenuItems" that needs to be marked as the active sort
	// ex: category column + desc
	const isSelectedSortMenuItem = useCallback(
		(sortBy: TSortBy) => {
			return sortMenuItems.find(
				(item: TDropdownMenuItem) =>
					item.id === sort.sortDir && sort.sortBy === sortBy
			);
		},
		[sort]
	);

	// Sort the drivers based on the natural order set by "naturalOrderCollator"
	useEffect(() => {
		setDrivers(getSortedDrivers(defaultDrivers));
	}, [defaultDrivers, getSortedDrivers]);

	useEffect(() => {
		defaultDrivers.length > 0 ? setCurrentPage(1) : setCurrentPage(0);
	}, [defaultDrivers]);

	return (
		<div
			ref={tableRef}
			className="drivers-list hmc-body-s justify-center overflow-scroll bg-layoutContainer md:overflow-auto"
		>
			<table
				className="w-full bg-layoutContainer text-systemInfo"
				data-testid="drivers-list"
			>
				<caption className="sr-only">{t("pages.drivers.list-caption")}</caption>
				<thead className="hmc-subtitle-s bg-layoutContainer text-systemInfo">
					<tr>
						{!filters.category && (
							<th className="w-1/12" data-testid="drivers-list-header-category">
								<div className="flex items-center gap-2">
									{t("drivers-list.header-category")}
									<SortButton
										isIconOnly
										kind={
											isSelectedSortMenuItem("category") ? "tertiary" : "ghost"
										}
										title={t("drivers-list.sort-category-title")}
										data-testid="drivers-list-sort-category"
										menuItems={sortMenuItems}
										selected={isSelectedSortMenuItem("category")}
										onSelection={(item: TDropdownMenuItem) => {
											onSort("category", item.id as TSortDir);
										}}
									>
										<HmcIcon
											name="more-m"
											data-testid="drivers-list-sort-category-icon"
										/>
									</SortButton>
								</div>
							</th>
						)}
						<th className="w-1/6" data-testid="drivers-list-header-subsystem">
							<div className="flex items-center gap-2 text-systemInfo">
								{t("drivers-list.header-subsystem")}
								<SortButton
									isIconOnly
									kind={
										isSelectedSortMenuItem("subsystem") ? "tertiary" : "ghost"
									}
									title={t("drivers-list.sort-subsystem-title")}
									data-testid="drivers-list-sort-subsystem"
									menuItems={sortMenuItems}
									selected={isSelectedSortMenuItem("subsystem")}
									onSelection={(item: TDropdownMenuItem) => {
										onSort("subsystem", item.id as TSortDir);
									}}
								>
									<HmcIcon
										name="more-m"
										data-testid="drivers-list-sort-subsystem-icon"
									/>
								</SortButton>
							</div>
						</th>
						<th className="w-1/6" data-testid="drivers-list-header-part-number">
							<div className="flex items-center gap-2 text-systemInfo">
								{t("drivers-list.header-part-number")}
								<SortButton
									isIconOnly
									kind={
										isSelectedSortMenuItem("partNumber") ? "tertiary" : "ghost"
									}
									title={t("drivers-list.sort-part-number-title")}
									data-testid="drivers-list-sort-part-number"
									menuItems={sortMenuItems}
									selected={isSelectedSortMenuItem("partNumber")}
									onSelection={(item: TDropdownMenuItem) => {
										onSort("partNumber", item.id as TSortDir);
									}}
								>
									<HmcIcon
										name="more-m"
										data-testid="drivers-list-sort-part-number-icon"
									/>
								</SortButton>
							</div>
						</th>
						<th
							className="text-systemInfo"
							data-testid="drivers-list-header-description"
						>
							{t("drivers-list.header-description")}
						</th>
						<th
							className=" text-systemInfo"
							data-testid="drivers-list-header-status"
						>
							{t("drivers-list.header-status")}
						</th>
						<th
							className="w-[110px] text-systemInfo"
							data-testid="drivers-list-header-actions"
						>
							{t("drivers-list.header-actions")}
						</th>
					</tr>
				</thead>
				<tbody>
					{currentItems.map((driver: TDriver) => {
						return (
							<tr
								key={`driver-${driver.id}-${driver.subsystem}`}
								className={
									driver.isObsolete ? "text-contentSilent" : "text-systemInfo"
								}
								data-testid="drivers-list-row"
							>
								{!filters.category && (
									<td data-testid="drivers-list-data-category">
										{driver.category}
									</td>
								)}
								<td data-testid="drivers-list-data-subsystem">
									{driver.subsystem}
								</td>
								<td
									className="hmc-subtitle-s"
									data-testid="drivers-list-data-part-number"
								>
									<Link
										title={t("drivers-list.details-link-title")}
										href={getDriversDetailsUrl(driver)}
									>
										{driver.partNumber}
									</Link>
								</td>
								<td data-testid="drivers-list-data-description">
									{driver.description}
								</td>
								<td
									className="w-[110px]"
									data-testid="drivers-list-data-status"
								>
									<DriverStatus isObsolete={driver.isObsolete} />
								</td>
								<td>
									<div className="flex items-center gap-2">
										<Link href={driver.repoUrl}>
											<HmcButton
												isIconOnly
												kind="tertiary"
												size="xs"
												isDisabled={!driver.repoUrl}
												title={t("drivers-list.action-repo-title")}
												data-testid="drivers-list-action-repo"
											>
												<RiGithubLine
													size={17}
													data-testid="drivers-list-action-repo-icon"
												/>
											</HmcButton>
										</Link>
										<Link href={driver.productUrl}>
											<HmcButton
												isIconOnly
												kind="tertiary"
												size="xs"
												isDisabled={!driver.productUrl}
												title={t("drivers-list.action-product-title")}
												data-testid="drivers-list-action-product"
											>
												<RiPagesLine
													size={15}
													data-testid="drivers-list-action-product-icon"
												/>
											</HmcButton>
										</Link>
									</div>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<div className="pagination-controls">
				<PaginationControls
					numOfDrivers={drivers.length}
					itemsPerPage={itemsPerPage}
					currentPage={currentPage}
					nextPage={nextPage}
					prevPage={prevPage}
					firstPage={firstPage}
					lastPage={lastPage}
				/>
			</div>
		</div>
	);
}
